import { useCallback, useEffect, useState } from "react";
import { useAuth } from "./auth-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useActiveCall } from "@/lib/api/calls";
import {
  INACTIVITY_WARNING_MS,
  msUntilInactivityLogout,
  touchLastActivity,
} from "@/lib/inactivity";
import { useMessageNotificationSound } from "@/lib/hooks/conversations/use-message-notification-sound";

export const InactivityMonitor = () => {
  const { revokeSession } = useAuth();
  const { data: activeCall } = useActiveCall();

  const { playWarningSound } = useMessageNotificationSound();

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const handleLogout = useCallback(async () => {
    try {
      await revokeSession();
      window.location.href = "/login";
    } catch {
      window.location.href = "/login";
    }
  }, [revokeSession]);

  useEffect(() => {
    let lastWrite = 0;
    const throttleMs = 1000;

    const handleLastActivity = () => {
      const now = Date.now();
      if (now - lastWrite < throttleMs) return;
      lastWrite = now;
      touchLastActivity();
    };

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];

    events.forEach((event) => {
      window.addEventListener(event, handleLastActivity);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleLastActivity);
      });
    };
  }, []);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();

      if (activeCall) {
        touchLastActivity(now);
        setShowWarning(false);
        return;
      }

      const msLeft = msUntilInactivityLogout(now);

      if (msLeft <= 0) {
        setShowWarning(false);
        handleLogout();
        return;
      }

      if (msLeft <= INACTIVITY_WARNING_MS) {
        setShowWarning(true);
        setCountdown(Math.max(0, Math.ceil(msLeft / 1000)));
      } else {
        setShowWarning(false);
      }
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [handleLogout, activeCall]);

  useEffect(() => {
    if (showWarning) {
      playWarningSound();
    }
  }, [showWarning, playWarningSound]);

  const progressValue =
    100 - (countdown / (INACTIVITY_WARNING_MS / 1000)) * 100;

  return (
    <Dialog open={showWarning} onOpenChange={() => {}}>
      <DialogContent hideCloseButton className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">
            Session Timeout Warning
          </DialogTitle>
          <DialogDescription className="text-center">
            Your session will expire due to inactivity. You will be
            automatically logged out in:
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center space-y-4 py-4">
          <div className="text-4xl font-bold tabular-nums text-red-600">
            {Math.floor(countdown / 60)}:
            {(countdown % 60).toString().padStart(2, "0")}
          </div>

          <div className="w-full">
            <Progress value={progressValue} className="h-2" />
          </div>

          <p className="text-center text-sm text-muted-foreground">
            This is for compliance and security purposes.
          </p>
        </div>

        <DialogFooter className="flex justify-center">
          <Button className="w-full">Stay Active</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
